import { useMemo } from 'react';
import { format } from 'date-fns';
import styled from 'styled-components';

import MoreContentRequester from 'components/MoreContentRequester';

import useDischargeCalendarDayInfinite from '../hooks/useDischargeCalendarDayInfinite';
import DischargeCalendarPatientCard from '../shared/DischargeCalendarPatientCard';
import { DayViewColumnSkeleton } from '../skeletons';
import { getPayerType } from '../utils/patientUtils';

interface DayViewColumnProps {
  date: Date;
}

export default function DayViewColumn({ date }: DayViewColumnProps) {
  const dayName = format(date, 'EEEE');
  const dayNumber = format(date, 'd');

  const infiniteQuery = useDischargeCalendarDayInfinite({ date });

  const entries = useMemo(() => {
    return infiniteQuery.data?.pages.flatMap((page) => page.data) ?? [];
  }, [infiniteQuery.data]);

  return (
    <Column data-cy={`day-view-column-${format(date, 'yyyy-MM-dd')}`}>
      <DayHeader>
        <DayName>{dayName}</DayName>
        <DayNumber>{dayNumber}</DayNumber>
      </DayHeader>
      <DayContent>
        {infiniteQuery.isLoading ? (
          <DayViewColumnSkeleton />
        ) : (
          entries.map((entry) => (
            <DischargeCalendarPatientCard key={entry.id} entry={entry} payerType={getPayerType(entry)} />
          ))
        )}
        <MoreContentRequester resource={infiniteQuery} />
      </DayContent>
    </Column>
  );
}

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 500px;
`;

const DayHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px 8px;
  background-color: ${({ theme }) => theme.colors.white};
  gap: 4px;
`;

const DayName = styled.span`
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black};
`;

const DayNumber = styled.span`
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black};
  line-height: 1;
`;

const DayContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  overflow-y: auto;
`;
